/**
 * TanStack Query hooks for the child_data_update change_request (#739/#737).
 * Create (producer-agnostic create API), Plan (read-only preview), and Process
 * (apply) — all calling the generated ChangeRequestsService.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

import { ChangeRequestsService } from '@/api/services/ChangeRequestsService';
import { ApiError } from '@/api';
import type {
  change_requests_CreateChangeRequestsRequest,
  change_requests_ChangeRequestInputDTO,
} from '@/api';
import { getRoutingToast } from '@/types/changeRequest';
import { workflowKeys } from './useWorkflows';

// Invalidate the cached data for each affected child after a change is applied.
function invalidateChildren(
  queryClient: ReturnType<typeof useQueryClient>,
  childIDs: string[],
): void {
  for (const id of childIDs) {
    queryClient.invalidateQueries({ queryKey: ['children', id] });
    queryClient.invalidateQueries({ queryKey: ['child-full', id] });
  }
}

/**
 * Create a change_request through the producer-agnostic create API
 * (`POST /api/v1/change-requests`) — the same path the email→LLM extractor and
 * the future app forms post to (only `source.kind` differs). Surfaces a toast
 * driven by the returned per-request `routing` class (pending / executed /
 * failed / manual / error), never a blanket success.
 */
export function useCreateChangeRequest() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      batch,
    }: {
      batch: change_requests_CreateChangeRequestsRequest;
      childIds: string[];
    }) => ChangeRequestsService.postApiV1ChangeRequests(batch),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      invalidateChildren(queryClient, variables.childIds ?? []);

      const result = res.results?.[0];
      const { kind, message } = getRoutingToast(i18n, result);
      switch (kind) {
        case 'error':
          toast.error(message);
          break;
        case 'warning':
          toast.warning(message);
          break;
        case 'info':
          toast.info(message);
          break;
        default:
          toast.success(message);
      }
    },
    onError: () => {
      toast.error(
        i18n._({
          id: 'changeRequest.toast.createFailed',
          message: 'Failed to create the change request',
        }),
      );
    },
  });
}

/**
 * Edit a PENDING change_request in place during triage review
 * (`PATCH /api/v1/change-requests/:id`). Stamps the server-authoritative
 * review.edited marker; the caller then approves via the generic transition route
 * (which records the `edited` outcome). Invalidates the item detail + lists so the
 * drawer re-reads the edited payload before approving. NO toast here — the
 * approve step that follows owns the user-facing feedback.
 */
export function useUpdateChangeRequestReview() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: change_requests_ChangeRequestInputDTO;
      childIds?: string[];
    }) => ChangeRequestsService.patchApiV1ChangeRequests(id, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      invalidateChildren(queryClient, variables.childIds ?? []);
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(
          i18n._({
            id: 'changeRequest.toast.editConflict',
            message: 'This change request can no longer be edited',
          }),
        );
        return;
      }
      toast.error(
        i18n._({
          id: 'changeRequest.toast.editError',
          message: 'Failed to save the edited change request',
        }),
      );
    },
  });
}

/**
 * Read-only dry-run preview for a change_request item.
 */
export function usePlanChildDataUpdate() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) =>
      ChangeRequestsService.getApiV1ChangeRequestsChildDataUpdatePlan(id),
    onError: () => {
      toast.error(
        i18n._({
          id: 'changeRequest.toast.planError',
          message: 'Failed to load the change preview',
        }),
      );
    },
  });
}

/**
 * Apply an approved change_request. Invalidates the inbox + each affected child.
 */
export function useProcessChildDataUpdate() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string; childIDs?: string[] }) =>
      ChangeRequestsService.postApiV1ChangeRequestsChildDataUpdateProcess(id),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });
      invalidateChildren(queryClient, variables.childIDs ?? []);

      // Process returns HTTP 200 even when some/all children failed; surface the
      // real per-batch outcome rather than a blanket success.
      if (data?.status === 'error') {
        toast.error(
          i18n._({ id: 'changeRequest.toast.applyAllFailed', message: 'The change could not be applied to any child' }),
        );
      } else if (data?.status === 'partial') {
        toast.warning(
          i18n._({ id: 'changeRequest.toast.applyPartial', message: 'Applied to some children; others failed' }),
        );
      } else {
        toast.success(
          i18n._({ id: 'changeRequest.toast.applied', message: 'Change request applied' }),
        );
      }
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(
          i18n._({
            id: 'changeRequest.toast.applyConflict',
            message: 'This change request must be approved before it can be applied',
          }),
        );
        return;
      }
      toast.error(
        i18n._({
          id: 'changeRequest.toast.applyError',
          message: 'Failed to apply the change request',
        }),
      );
    },
  });
}
